/**
 * Chart series palette — ordered, deterministic.
 *
 * Use for Recharts series fills/strokes when you need N distinct colors for
 * categorical data. Replaces ad-hoc per-product arrays
 * (e.g. `products/lens/app/projects/[slug]/page.tsx`'s LANG_CHART_COLORS).
 *
 * Hex values live in `excellent/app/globals.css` — same in light + dark.
 *
 * Index meaning is conventional: chart[0] = primary series, chart[1] =
 * secondary, etc. The palette LEADS WITH INDIGO (the brand color) — every
 * single-series chart should use `chartColor(0)` / `CHART_PALETTE[0]` so the
 * default chart color across the app is indigo, not amber or black. Don't
 * reorder without auditing every chart caller, since downstream callers rely
 * on "the first series is indigo".
 */
export const CHART_PALETTE = [
  "var(--chart-1)", // indigo  — primary series / brand
  "var(--chart-2)", // emerald — success / second series
  "var(--chart-3)", // violet  — neutral 3rd hue
  "var(--chart-4)", // amber   — attention / warm series
  "var(--chart-5)", // pink    — distinct 5th hue
] as const;

export type ChartSeriesIndex = 0 | 1 | 2 | 3 | 4;

/**
 * Pick chart color N, wrapping for indices ≥ 5. Convenience for
 * dynamically-sized series lists.
 */
export function chartColor(index: number): string {
  // length is always ≥ 1, so the index lookup is always defined;
  // we assert to satisfy noUncheckedIndexedAccess.
  return CHART_PALETTE[index % CHART_PALETTE.length] as string;
}

/**
 * Lens tool-category palette. Each Claude Code tool gets a stable color
 * across the product. Use when visualizing tool usage; do NOT use these
 * for general-purpose chart series (that's `CHART_PALETTE`).
 *
 * Hex values live in `excellent/app/globals.css` as `--viz-tool-*`.
 */
export const VIZ_TOOL_COLORS = {
  read: "var(--viz-tool-read)",
  write: "var(--viz-tool-write)",
  edit: "var(--viz-tool-edit)",
  grep: "var(--viz-tool-grep)",
  glob: "var(--viz-tool-glob)",
  file_io: "var(--viz-tool-file-io)",
  shell: "var(--viz-tool-shell)",
  agent: "var(--viz-tool-agent)",
  web: "var(--viz-tool-web)",
  planning: "var(--viz-tool-planning)",
  todo: "var(--viz-tool-todo)",
  skill: "var(--viz-tool-skill)",
  mcp: "var(--viz-tool-mcp)",
  other: "var(--viz-tool-other)",
} as const;

export type VizToolKey = keyof typeof VIZ_TOOL_COLORS;
